/**
 * Phantom Context — converts a ContinuitySnapshot into synthetic Task and
 * Assignment objects that the optimizer seeds into its constraint-checking
 * indexes (taskMap, assignmentsByParticipant) so HC-5, HC-12, HC-14, and
 * HC-15 are enforced across schedule boundaries.  Phantom rest rule durations
 * are collected so HC-14 can resolve cross-schedule rule thresholds; phantom
 * tasks also carry their sleepRecovery rule + shiftIndex so HC-15's recovery
 * window can open across the boundary.
 *
 * Phantom tasks/assignments are NEVER added to the output Schedule — they
 * exist only in the optimizer's internal indexes during generation.
 */

import { type ContinuityAssignment, ContinuityParticipant, type ContinuitySnapshot } from '../models/continuity-schema';
import { type Assignment, AssignmentStatus, type Participant, type Task } from '../models/types';
import { clampRestRuleHours } from '../shared/utils/rest-rule-clamp';

// ─── Public Types ───────────────────────────────────────────────────────────

export interface PhantomContext {
  /** Synthetic Task objects for constraint resolution via taskMap. */
  phantomTasks: Task[];
  /** Synthetic Assignment objects for per-participant indexes. */
  phantomAssignments: Assignment[];
  /** Set of phantom task IDs for easy identification/filtering. */
  phantomTaskIds: Set<string>;
  /** Snapshotted rest rule durations from the previous schedule (ruleId → durationMs). */
  phantomRestRules: Map<string, number>;
}

// ─── Builder ────────────────────────────────────────────────────────────────

/**
 * Build a PhantomContext from a parsed ContinuitySnapshot and the new
 * schedule's participant list.
 *
 * Matching is by exact participant name.  Participants in the snapshot
 * but absent from `participants` are silently ignored.
 */
export function buildPhantomContext(snapshot: ContinuitySnapshot, participants: Participant[]): PhantomContext {
  const phantomTasks: Task[] = [];
  const phantomAssignments: Assignment[] = [];
  const phantomTaskIds = new Set<string>();
  const phantomRestRules = new Map<string, number>();

  // Build name → new-participant lookup
  const byName = new Map<string, Participant>();
  for (const p of participants) {
    byName.set(p.name, p);
  }

  let idx = 0;

  for (const cp of snapshot.participants) {
    const matched = byName.get(cp.name);
    if (!matched) continue; // participant not in new schedule

    for (const ca of cp.assignments) {
      // Defense-in-depth, mirroring the restRuleDurationHours / sleepRecovery
      // guards below: a phantom whose interval is missing, unparseable, or
      // INVERTED is worse than no phantom at all. An empty span makes HC-5,
      // HC-12, HC-14 and HC-15 all silently fail open against it (the gap
      // arithmetic turns positive, the HC-15 recovery window anchored on `end`
      // opens before the shift, `hasLoadDuringOverlap` bails on the empty
      // overlap) — and because phantoms never reach `schedule.violations`
      // nothing surfaces it.
      //
      // Worse, the damage is not confined to phantom↔real pairs: an inverted
      // block can be ABSORBED by `coalesceTaskRuns` into a REAL split half's
      // run (it only needs `tStart === runEnd`, the same `sourceName`, and a
      // split first member — a phantom carries no `splitGroupId` of its own but
      // satisfies `splitInvolved` via that first member), and `runEnd = tEnd`
      // then drags the run's evaluated end BACKWARDS. Measured: two real
      // over-K split halves touching at a boundary go from 1 HC-12 violation to
      // 0 — a real↔real fail-open — and in the other sort tie-break order the
      // same input yields 3 spurious violations, so it is non-deterministic too.
      //
      // The web import boundary rejects these outright
      // (`validateAssignmentEntry`); this skip covers the non-web callers
      // (benchmarks, fixtures) that build a context without that validator.
      const startMs = new Date(ca.timeBlock?.start).getTime();
      const endMs = new Date(ca.timeBlock?.end).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;

      const taskId = `phantom-${idx}`;
      const asgnId = `phantom-asgn-${idx}`;
      idx++;

      const task = continuityAssignmentToTask(taskId, ca);
      phantomTasks.push(task);
      phantomTaskIds.add(taskId);

      // Collect snapshotted rest rule durations for cross-schedule HC-14.
      // Defense-in-depth: require a finite positive number (not just != null) so a
      // malformed snapshot that slipped past validation degrades to a safe skip —
      // the rule id is simply not added and HC-14 falls back to the live rule map —
      // rather than seeding a NaN/non-positive duration that silently disables HC-14.
      //
      // The surviving value is then CLAMPED to the same [0.5, 24] range the live
      // catalog enforces. This is the second engine-wide writer into
      // `SchedulingEngine.restRuleMap` (`mergePhantomRules` merges it beside
      // `buildRestRuleMap`'s already-clamped entries), and HC-14's only test is
      // `gap < durationMs`, so an unbounded value is the mirror image of the
      // non-positive one guarded above: it makes the comparison ALWAYS true.
      // That is not confined to phantom pairs. `mergePhantomRules` deliberately
      // merges a rule id the live catalog does not know (a snapshot from another
      // planner/device), and a live template may carry exactly such an id — an
      // acknowledged product state that preflight reports as `ORPHAN_REST_RULE`
      // and lets through. `checkRestRules` gates HC-14 membership on
      // `restRuleMap.has(...)`, so the merge ARMS HC-14 for those REAL tasks, and
      // its same-rule Phase 1 has no `Math.min` bound (unlike the cross-rule
      // Phase 2) — every real pair sharing that id would be flagged.
      //
      // Clamp the HOURS, not the milliseconds: the guard above tests
      // `Number.isFinite` on the HOURS, and `1e308` is finite while
      // `1e308 * 3600000` overflows to `Infinity` — which then serializes into
      // `Schedule.restRuleSnapshot` as JSON `null` and reloads (via
      // `sanitizeRestRuleSnapshot`) as 0.5h, flipping HC-14 from maximally
      // over-strict to effectively off across a reload.
      //
      // Identity for every legitimate value (`addRestRule`/`updateRestRule` and
      // the task-rules input already bound it to [0.5, 24], and both
      // `exportDaySnapshot` call sites pre-clamp) ⇒ zero regression. The web
      // import boundary rejects out-of-range values outright
      // (`validateAssignmentEntry`); this clamp covers the non-web callers
      // (benchmarks, fixtures) that build a context without that validator —
      // the same two-layer posture as the timeBlock-ordering guard above.
      if (
        ca.restRuleId &&
        typeof ca.restRuleDurationHours === 'number' &&
        Number.isFinite(ca.restRuleDurationHours) &&
        ca.restRuleDurationHours > 0 &&
        !phantomRestRules.has(ca.restRuleId)
      ) {
        phantomRestRules.set(ca.restRuleId, clampRestRuleHours(ca.restRuleDurationHours) * 3600000);
      }

      phantomAssignments.push({
        id: asgnId,
        taskId,
        slotId: 'phantom-slot',
        participantId: matched.id,
        status: AssignmentStatus.Scheduled,
        updatedAt: new Date(),
      });
    }
  }

  return { phantomTasks, phantomAssignments, phantomTaskIds, phantomRestRules };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Convert a ContinuityAssignment into a minimal Task with constraint-relevant fields. */
function continuityAssignmentToTask(taskId: string, ca: ContinuityAssignment): Task {
  // HC-15 (D18): carry the recovery rule so getRecoveryWindow can open a window
  // for this phantom across the boundary. Defense-in-depth (mirrors the
  // restRuleDurationHours guard above): only attach a well-formed rule — a
  // non-empty triggerShifts array and a finite recoveryHours > 0 — so a
  // malformed snapshot degrades to a safe no-window skip rather than seeding a
  // NaN/non-positive window (no recovery) or a huge value (an effectively
  // infinite, over-blocking window). triggerShifts is copied to avoid aliasing.
  const sr = ca.sleepRecovery;
  const sleepRecovery =
    sr &&
    Array.isArray(sr.triggerShifts) &&
    sr.triggerShifts.length > 0 &&
    typeof sr.recoveryHours === 'number' &&
    Number.isFinite(sr.recoveryHours) &&
    sr.recoveryHours > 0
      ? { triggerShifts: [...sr.triggerShifts], recoveryHours: sr.recoveryHours }
      : undefined;

  return {
    id: taskId,
    name: ca.taskName,
    sourceName: ca.sourceName,
    timeBlock: {
      start: new Date(ca.timeBlock.start),
      end: new Date(ca.timeBlock.end),
    },
    requiredCount: 0,
    slots: [],
    // Same defense-in-depth posture: a non-numeric weight survives `clamp01` as
    // NaN (Math.min/Math.max coerce), and NaN fails HC-15's `weight > 0` test
    // OPEN. Degrading to `undefined` routes through `getTaskBaseLoadWeight`'s
    // `?? 1`, i.e. "fully loaded" — the conservative direction for a safety
    // constraint, and the schema's own default. Finite values (including
    // out-of-range ones) pass through untouched; clamp01 bounds them at read.
    baseLoadWeight:
      typeof ca.baseLoadWeight === 'number' && Number.isFinite(ca.baseLoadWeight) ? ca.baseLoadWeight : undefined,
    loadWindows: ca.loadWindows?.map((lw) => ({
      id: lw.id,
      startHour: lw.startHour,
      startMinute: lw.startMinute,
      endHour: lw.endHour,
      endMinute: lw.endMinute,
      weight: lw.weight,
      blocksAtBoundary: lw.blocksAtBoundary,
    })),
    sameGroupRequired: false,
    blocksConsecutive: ca.blocksConsecutive,
    restRuleId: ca.restRuleId,
    sleepRecovery,
    shiftIndex: ca.shiftIndex,
    color: ca.color || '#95A5A6',
  };
}

/**
 * Merge snapshotted rest rule durations from phantom context into the
 * active rest rule map.  Only adds entries for rule IDs that are NOT
 * already present — the current schedule's rules take precedence.
 *
 * `deletedRuleIds` carries the ids the user has SOFT-DELETED (rest-rule
 * tombstones).  Absence from `baseMap` alone cannot distinguish the two very
 * different reasons an id can be missing, and they need opposite handling:
 *
 *   - the rule never existed locally (snapshot from another planner/device, a
 *     factory reset since the export) → MERGE, this is the whole point of the
 *     function: HC-14 can only resolve a cross-boundary gap if it knows the
 *     duration, and only the snapshot carries it.
 *   - the rule was deleted → SKIP.  `buildRestRuleMap` omits tombstones
 *     precisely so HC-14 stops being enforced for that id, and preflight
 *     PROMISES the user exactly that ("…מפנה לכלל מרווחים שנמחק — HC-14 לא
 *     ייאכף עבורה").  Merging it back silently breaks that promise, and not
 *     just for the phantom pairs the merge exists to serve: `restRuleMap` is a
 *     single engine-wide map, so a resurrected id re-arms HC-14 for REAL↔REAL
 *     pairs of live tasks whose template still carries the orphaned
 *     `restRuleId` (`removeRestRule` is soft-only, so it always does).  Worse,
 *     the merged map is frozen into `Schedule.restRuleSnapshot`, reloaded
 *     verbatim, and re-exported into the next continuity snapshot — so absent
 *     this filter the resurrection self-perpetuates with no decay path.
 *
 * The parameter is optional so non-web callers (benchmarks, unit tests) that
 * have no tombstone concept keep the previous union semantics unchanged.
 */
export function mergePhantomRules(
  baseMap: Map<string, number>,
  context: PhantomContext,
  deletedRuleIds?: ReadonlySet<string>,
): Map<string, number> {
  if (context.phantomRestRules.size === 0) return baseMap;
  const merged = new Map(baseMap);
  for (const [ruleId, durationMs] of context.phantomRestRules) {
    if (deletedRuleIds?.has(ruleId)) continue; // deliberately turned off by the user
    if (!merged.has(ruleId)) merged.set(ruleId, durationMs);
  }
  return merged;
}
